import { useEffect, useState } from "react";
import { Star, Save, RotateCcw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import {
  GRADE_PRICING_KEY,
  gradePricingQueryKey,
  useGradePricing,
  type GradePricingValues,
} from "@/hooks/useGradePricing";

const TIER: Record<keyof GradePricingValues, string> = { A: "Premium", B: "Good", C: "Fair" };

export default function GradePricingSection() {
  const { id: companyId, name: companyName } = useCurrentCompany();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading } = useGradePricing(companyId);
  const [values, setValues] = useState<GradePricingValues>({ A: 75, B: 65, C: 50 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setValues(data.effective);
  }, [data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: gradePricingQueryKey(companyId) });

  const save = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      const { data: rows, error } = await supabase
        .from("company_settings")
        .upsert(
          { company_id: companyId, setting_key: GRADE_PRICING_KEY, setting_value: values as any, updated_by: user?.id ?? null },
          { onConflict: "company_id,setting_key" },
        )
        .select("id");
      if (error) throw error;
      if (!rows || rows.length === 0) throw new Error("You don't have permission to change grade pricing for this company.");
      await refresh();
      toast.success(`Grade pricing saved for ${companyName ?? "this company"}`);
    } catch (e: any) {
      toast.error(e?.message || "Could not save grade pricing");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      const { data: rows, error } = await supabase
        .from("company_settings")
        .delete()
        .eq("company_id", companyId)
        .eq("setting_key", GRADE_PRICING_KEY)
        .select("id");
      if (error) throw error;
      if (!rows || rows.length === 0) throw new Error("Nothing was reset — you may not have permission.");
      await refresh();
      toast.success("Reverted to the platform default");
    } catch (e: any) {
      toast.error(e?.message || "Could not reset grade pricing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="grade-pricing" className="bg-card rounded-xl border border-border p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Star className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-semibold text-foreground">Refurbishment grade pricing</h2>
            {data && (
              <Badge variant={data.isOverride ? "default" : "secondary"}>
                {data.isOverride ? `Custom for ${companyName ?? "this company"}` : "Platform default"}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Retail price as a percentage of new, per grade.
            {data && !data.isOverride && ` Using platform default — save to override for ${companyName ?? "this company"}.`}
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-3">
          {(["A", "B", "C"] as const).map((g) => (
            <div key={g} className="flex items-center gap-4">
              <span className="w-32 text-sm font-medium text-foreground">Grade {g} ({TIER[g]})</span>
              <Input
                type="number"
                min={0}
                max={100}
                value={values[g]}
                onChange={(e) => setValues({ ...values, [g]: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">% of retail</span>
              {data?.isOverride && (
                <span className="text-xs text-muted-foreground">Platform: {data.platformDefault[g]}%</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving || !companyId} className="gap-2">
          <Save className="w-4 h-4" /> Save
        </Button>
        {data?.isOverride && (
          <Button variant="outline" onClick={reset} disabled={saving} className="gap-2">
            <RotateCcw className="w-4 h-4" /> Reset to platform default
          </Button>
        )}
      </div>
    </section>
  );
}
